/**
 * Income Page - Admin Dashboard
 * View and manage all income entries
 */
import { useState, useEffect, useRef } from 'react';
import { api, type Income, type IncomeCategory, type User } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useOrganizationChange } from '../hooks/useOrganizationChange';

export function IncomePage() {
  const { token } = useAuth();
  const [incomes, setIncomes] = useState<Income[]>([]);
  const [categories, setCategories] = useState<IncomeCategory[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [userSearch, setUserSearch] = useState('');
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingIncome, setEditingIncome] = useState<Income | null>(null);

  // Category management
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<IncomeCategory | null>(null);
  const [newCategoryName, setNewCategoryName] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    amount: '',
    description: '',
    date: new Date().toISOString().split('T')[0],
    categoryId: '',
    notes: '',
    userId: '',
  });

  // Refresh when organization changes
  useOrganizationChange(() => {
    setPage(1);
    loadData();
    loadCategories();
  });

  useEffect(() => {
    if (token) {
      api.setToken(token);
      loadData();
    }
  }, [token, page, search, selectedCategory]);

  useEffect(() => {
    if (token) {
      api.setToken(token);
      loadCategories();
      loadUsers();
    }
  }, [token]);

  const loadCategories = async () => {
    const result = await api.getIncomeCategories();
    if (result.success && result.data) {
      setCategories(result.data.categories);
    }
  };

  const loadUsers = async (search?: string) => {
    const result = await api.getUsers(search);
    if (result.success && result.data) {
      setUsers(result.data.users);
    }
  };

  const loadData = async () => {
    setIsLoading(true);
    const result = await api.getIncomes({
      page,
      limit: 20,
      search: search || undefined,
      category: selectedCategory || undefined,
    });
    if (result.success && result.data) {
      setIncomes(result.data.income);
      setTotalPages(result.data.pagination.totalPages);
    }
    setIsLoading(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Use category name as description if description is empty and category is selected
    let description = formData.description;
    if (!description && formData.categoryId) {
      const category = categories.find(c => c.id === formData.categoryId);
      description = category?.name || '';
    }

    const data = {
      amount: parseFloat(formData.amount),
      description,
      date: formData.date,
      categoryId: formData.categoryId || null,
      notes: formData.notes || null,
      userId: formData.userId || undefined,
    };

    let result;
    if (editingIncome) {
      result = await api.updateIncome(editingIncome.id, data);
    } else {
      result = await api.createIncome(data);
    }

    if (result.success) {
      setShowAddModal(false);
      setEditingIncome(null);
      resetForm();
      loadData();
    } else {
      alert(result.error?.message || 'Failed to save income');
    }
  };

  const handleDelete = async (income: Income) => {
    if (!confirm('Move this income to trash?')) return;

    const result = await api.deleteIncome(income.id);
    if (result.success) {
      loadData();
    } else {
      alert(result.error?.message || 'Failed to delete');
    }
  };

  const handlePermanentDelete = async (income: Income) => {
    if (!confirm('Permanently delete this income? This cannot be undone.')) return;

    const result = await api.permanentDeleteIncome(income.id);
    if (result.success) {
      loadData();
    } else {
      alert(result.error?.message || 'Failed to delete');
    }
  };

  const handleRestore = async (income: Income) => {
    const result = await api.restoreIncome(income.id);
    if (result.success) {
      loadData();
    } else {
      alert(result.error?.message || 'Failed to restore');
    }
  };

  // Category management functions
  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) return;

    const result = await api.createIncomeCategory(newCategoryName.trim());
    if (result.success) {
      setNewCategoryName('');
      loadCategories();
    } else {
      alert(result.error?.message || 'Failed to add category');
    }
  };

  const handleUpdateCategory = async () => {
    if (!editingCategory || !newCategoryName.trim()) return;

    const result = await api.updateIncomeCategory(editingCategory.id, newCategoryName.trim());
    if (result.success) {
      setEditingCategory(null);
      setNewCategoryName('');
      loadCategories();
    } else {
      alert(result.error?.message || 'Failed to update category');
    }
  };

  const handleDeleteCategory = async (category: IncomeCategory) => {
    if (!confirm(`Delete category "${category.name}"?`)) return;

    const result = await api.deleteIncomeCategory(category.id);
    if (result.success) {
      loadCategories();
    } else {
      alert(result.error?.message || 'Failed to delete category');
    }
  };

  const openEditModal = (income: Income) => {
    setEditingIncome(income);
    setFormData({
      amount: income.amount.toString(),
      description: income.description,
      date: income.date,
      categoryId: income.categoryId || '',
      notes: income.notes || '',
      userId: income.userId || '',
    });
    setUserSearch(income.username || '');
    setShowAddModal(true);
  };

  const resetForm = () => {
    setFormData({
      amount: '',
      description: '',
      date: new Date().toISOString().split('T')[0],
      categoryId: '',
      notes: '',
      userId: '',
    });
    setUserSearch('');
    setShowUserDropdown(false);
  };

  const formatCurrency = (amount: number, currency: string) => {
    return `${currency} ${amount.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="p-4 lg:p-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Income</h1>
        <div className="flex gap-2">
          <button
            onClick={() => setShowCategoryModal(true)}
            className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center gap-2"
          >
            <span>Categories</span>
          </button>
          <button
            onClick={() => {
              resetForm();
              setEditingIncome(null);
              setShowAddModal(true);
            }}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
          >
            <span>+</span>
            <span>Add Income</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <input
            type="text"
            placeholder="Search income..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
          />
          <select
            value={selectedCategory}
            onChange={(e) => {
              setSelectedCategory(e.target.value);
              setPage(1);
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
          >
            <option value="">All Categories</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Income List */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="text-gray-500">Loading...</div>
        </div>
      ) : incomes.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm p-12 text-center">
          <div className="text-gray-400 text-6xl mb-4">💰</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No income found</h3>
          <p className="text-gray-500">Add your first income entry to get started.</p>
        </div>
      ) : (
        <>
          {/* Desktop Table */}
          <div className="hidden lg:block bg-white rounded-lg shadow-sm overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {incomes.map((income) => (
                  <tr key={income.id} className={income.isDeleted ? 'bg-red-50' : ''}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {income.description}
                      </div>
                      {income.notes && (
                        <div className="text-sm text-gray-500 truncate max-w-xs">
                          {income.notes}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                        {income.categoryName || 'Uncategorized'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {income.username}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(income.date)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <span className="text-sm font-semibold text-green-600">
                        +{formatCurrency(income.amount, income.currency)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      {income.isDeleted ? (
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => handleRestore(income)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            Restore
                          </button>
                          <button
                            onClick={() => handlePermanentDelete(income)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Delete
                          </button>
                        </div>
                      ) : (
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => openEditModal(income)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(income)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="lg:hidden space-y-4">
            {incomes.map((income) => (
              <div
                key={income.id}
                className={`bg-white rounded-lg shadow-sm p-4 ${
                  income.isDeleted ? 'border-l-4 border-red-500' : ''
                }`}
              >
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h3 className="font-medium text-gray-900">{income.description}</h3>
                    <p className="text-sm text-gray-500">{income.username}</p>
                  </div>
                  <span className="text-lg font-semibold text-green-600">
                    +{formatCurrency(income.amount, income.currency)}
                  </span>
                </div>
                <div className="flex items-center gap-2 mb-3">
                  <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                    {income.categoryName || 'Uncategorized'}
                  </span>
                  <span className="text-sm text-gray-500">{formatDate(income.date)}</span>
                </div>
                {income.notes && (
                  <p className="text-sm text-gray-500 mb-3">{income.notes}</p>
                )}
                <div className="flex gap-2 border-t pt-3">
                  {income.isDeleted ? (
                    <>
                      <button
                        onClick={() => handleRestore(income)}
                        className="flex-1 text-center py-2 text-blue-600 font-medium"
                      >
                        Restore
                      </button>
                      <button
                        onClick={() => handlePermanentDelete(income)}
                        className="flex-1 text-center py-2 text-red-600 font-medium"
                      >
                        Delete
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => openEditModal(income)}
                        className="flex-1 text-center py-2 text-blue-600 font-medium"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(income)}
                        className="flex-1 text-center py-2 text-red-600 font-medium"
                      >
                        Delete
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center gap-2 mt-6">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-4 py-2 border rounded-lg disabled:opacity-50"
              >
                Previous
              </button>
              <span className="px-4 py-2">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-4 py-2 border rounded-lg disabled:opacity-50"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h2 className="text-xl font-bold mb-4">
              {editingIncome ? 'Edit Income' : 'Add Income'}
            </h2>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Amount *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    required
                    value={formData.amount}
                    onChange={(e) =>
                      setFormData({ ...formData, amount: e.target.value })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  />
                </div>
                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    User *
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="Search user..."
                    value={userSearch}
                    onChange={(e) => {
                      const value = e.target.value;
                      setUserSearch(value);
                      setShowUserDropdown(true);
                      if (!value) {
                        setFormData({ ...formData, userId: '' });
                      }
                      // Debounced search
                      if (searchTimeoutRef.current) {
                        clearTimeout(searchTimeoutRef.current);
                      }
                      searchTimeoutRef.current = setTimeout(() => {
                        loadUsers(value || undefined);
                      }, 300);
                    }}
                    onFocus={() => {
                      setShowUserDropdown(true);
                      loadUsers(userSearch || undefined);
                    }}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  />
                  {showUserDropdown && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {users.map((user) => (
                          <div
                            key={user.id}
                            onClick={() => {
                              setFormData({ ...formData, userId: user.id });
                              setUserSearch(user.username);
                              setShowUserDropdown(false);
                            }}
                            className="px-4 py-2 cursor-pointer hover:bg-green-50 text-sm"
                          >
                            {user.username}
                          </div>
                        ))}
                      {users.length === 0 && (
                        <div className="px-4 py-2 text-sm text-gray-500">No users found</div>
                      )}
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category *
                  </label>
                  <select
                    required
                    value={formData.categoryId}
                    onChange={(e) =>
                      setFormData({ ...formData, categoryId: e.target.value })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  >
                    <option value="">Select category</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date *
                  </label>
                  <input
                    type="date"
                    required
                    value={formData.date}
                    onChange={(e) =>
                      setFormData({ ...formData, date: e.target.value })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description (optional)
                  </label>
                  <input
                    type="text"
                    placeholder="Optional - uses category name if empty"
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes
                  </label>
                  <textarea
                    value={formData.notes}
                    placeholder="Additional notes (optional)"
                    onChange={(e) =>
                      setFormData({ ...formData, notes: e.target.value })
                    }
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  />
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setEditingIncome(null);
                  }}
                  className="flex-1 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  {editingIncome ? 'Update' : 'Add'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Category Management Modal */}
      {showCategoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Manage Categories</h2>
              <button
                onClick={() => {
                  setShowCategoryModal(false);
                  setEditingCategory(null);
                  setNewCategoryName('');
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Add New Category */}
            <div className="mb-4">
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder={editingCategory ? 'Edit category name' : 'New category name'}
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      editingCategory ? handleUpdateCategory() : handleAddCategory();
                    }
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                />
                {editingCategory ? (
                  <>
                    <button
                      onClick={handleUpdateCategory}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                    >
                      Save
                    </button>
                    <button
                      onClick={() => {
                        setEditingCategory(null);
                        setNewCategoryName('');
                      }}
                      className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <button
                    onClick={handleAddCategory}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    Add
                  </button>
                )}
              </div>
            </div>

            {/* Category List */}
            <div className="space-y-2">
              {categories.map((category) => (
                <div
                  key={category.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{category.name}</span>
                    {category.isSystem && (
                      <span className="text-xs text-gray-500 bg-gray-200 px-2 py-0.5 rounded">
                        System
                      </span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setEditingCategory(category);
                        setNewCategoryName(category.name);
                      }}
                      className="text-blue-600 hover:text-blue-800 text-sm"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteCategory(category)}
                      className="text-red-600 hover:text-red-800 text-sm"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
